import type {ReactElement} from 'react';
import React, {useRef, useEffect, useState} from 'react';
import {Panel, PanelFooter, PanelFooterPrimaryDefaultAction, PanelFooterSecondaryDefaultAction, PanelHeader} from '@Components/panel';
import {toggleIsFbPanelOpenState} from '@Panels/user-media-panel/user-media-panel-reducer';
import {getUserId, isUserLoggedIn} from '@Libraries/user.library';
import {openErrorModal} from '@Modals/error-modal';
import type {GridUserMediaItemStorage} from '@Libraries/user-media-library';
import {checkUploadStatus, UserMediaType} from '@Libraries/user-media-library';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import type {AlbumData} from '@Panels/user-media-panel/user-media-panel.types';
import {COOKIE_FB_SESSION_KEY, FACEBOOK_PHOTOS_GRID} from '@Panels/user-media-panel/user-media-panel.types';
import {Text} from '@Components/text';
import {getSelectedGridItems} from '@Components/base-grid';
import type {UserImageVOResponse} from '@Libraries/user-image-library';
import {getGridUserImageItemStorageForUserImageVO} from '@Components/base-grid/components/grid-user-image-item';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {isIOSAppOrEmbeddedEditor} from '@Utils/browser.util';
import {deleteCookie, readCookie, setCookie} from '@Utils/cookie.util';
import {onError} from '@Panels/user-menu-panel/user-menu-panel-helper';
import {FacebookAlbumPhotos} from '../facebook-album-photos';
import {FacebookAlbums} from '../facebook-albums';
import {ConnectToFacebook} from '../connect-to-facebook';
import {useAppDispatch, useAppSelector} from '@/hooks';

interface FacebookMediaSectionProps {
  maxSelectionNumber?: number;

  onClose(): void;

  onStartAddSelected?: () => void;

  onAddSelected(items: GridUserMediaItemStorage): void;
}

interface CurrentFbIdResponse {
  fbId?: string;
  fbSessionKey?: string;
  oauthUrl?: string;
}

export function FacebookMediaSection({onStartAddSelected = (): void => {}, ...props}: FacebookMediaSectionProps): ReactElement {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<AlbumData | null>(null);
  const oauthUrl = useRef('');
  const selectedGridItems = useAppSelector((state) => {
    return getSelectedGridItems(state, FACEBOOK_PHOTOS_GRID) as Array<GridUserMediaItemStorage>;
  });
  const dispatch = useAppDispatch();

  useEffect(() => {
    void initialize();
  }, []);

  const initialize = async (): Promise<void> => {
    const fbSessionKey = readCookie(COOKIE_FB_SESSION_KEY);
    if (fbSessionKey) {
      setIsLoggedIn(true);
      return;
    }
    setIsLoading(true);
    const response = (await window.PMW.readLocal('authenticate/getCurrentUserFacebookId', {
      isIOSAppOrEmbeddedEditor: isIOSAppOrEmbeddedEditor(),
      version2: true,
    })) as CurrentFbIdResponse;
    if (isUserLoggedIn() && response.fbId && parseInt(response.fbId, 10)) {
      setIsLoggedIn(true);
      setIsLoading(false);
    } else if (response.fbSessionKey) {
      setCookie(COOKIE_FB_SESSION_KEY, response.fbSessionKey, 1);
      setIsLoggedIn(true);
      setIsLoading(false);
    } else if (response.oauthUrl) {
      setIsLoading(false);
      oauthUrl.current = response.oauthUrl;
    }
  };

  const onBack = (): void => {
    if (selectedAlbum) {
      setSelectedAlbum(null);
    } else {
      dispatch(toggleIsFbPanelOpenState(false));
    }
  };

  const getHeader = (): ReactElement => {
    return (
      <PanelHeader
        title={selectedAlbum ? `${window.i18next.t('pmwjs_facebook')} (${selectedAlbum.name})` : window.i18next.t('pmwjs_facebook_photos')}
        showBackButton
        onBackButton={onBack}
        onClose={(): void => {
          props.onClose();
        }}
      />
    );
  };

  const addSelectedItems = async (): Promise<void> => {
    let error = false;

    props.onClose();
    showLoading('adding-facebook-photo');
    onStartAddSelected();

    for (const item of selectedGridItems) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const data = (await window.PMW.pollingAjaxCallAsync(
          () => {
            return window.PMW.writeLocal('posterimage/facebookupload', {
              userId: getUserId(),
              pid: item.id,
              url: item.data.url as string,
            });
          },
          checkUploadStatus.bind(null, UserMediaType.IMAGE)
        )) as UserImageVOResponse;
        props.onAddSelected(getGridUserImageItemStorageForUserImageVO(data));
      } catch (e) {
        console.error(e);
        error = true;
      }
    }
    hideLoading('adding-facebook-photo');

    if (error) {
      openErrorModal();
    }
  };

  const unlinkAccount = async (): Promise<void> => {
    deleteCookie(COOKIE_FB_SESSION_KEY);
    if (isUserLoggedIn()) {
      try {
        await window.PMW.readLocal('user/unlinkFbPhotosSession');
      } catch (_) {
        onError();
      }
    }
    dispatch(toggleIsFbPanelOpenState(false));
  };

  const getFooterPrimaryActions = (): Array<ReactElement> => {
    if (!selectedGridItems.length) {
      return [];
    }
    return [<PanelFooterPrimaryDefaultAction key="btn-add-selected" text={window.i18next.t('pmwjs_add_selected')} onClick={addSelectedItems} />];
  };

  const getFooterSecondaryActions = (): Array<ReactElement> => {
    return [<PanelFooterSecondaryDefaultAction key="btn-add-unlink" text={window.i18next.t('pmwjs_unlink_pos_account')} onClick={unlinkAccount} />];
  };

  const getFooter = (): ReactElement | null => {
    if (!isLoggedIn) {
      return null;
    }
    return <PanelFooter primaryActions={getFooterPrimaryActions()} secondaryActions={getFooterSecondaryActions()} />;
  };

  const getContent = (): ReactElement => {
    if (isLoading) {
      return (
        <div className="_full-width _full-height flex-center flex-v-row">
          <div>
            <CircularProgressLoader size={LOADER_SIZE.LARGE} />
          </div>
          <Text val={window.i18next.t('pmwjs_connecting_to_facebook')} className="content-body" />
        </div>
      );
    }

    if (!isLoggedIn) {
      return (
        <ConnectToFacebook
          oauthUrl={oauthUrl.current}
          onConnecting={(): void => {
            setIsLoading(true);
          }}
          onLoggedIn={(): void => {
            setIsLoading(false);
            setIsLoggedIn(true);
          }}
        />
      );
    }

    if (selectedAlbum) {
      return (
        <FacebookAlbumPhotos
          selectedAlbum={selectedAlbum}
          maxSelectionNumber={props.maxSelectionNumber}
          onAddSelected={() => {
            void addSelectedItems();
          }}
        />
      );
    }

    return (
      <FacebookAlbums
        onAlbumSelected={(album: AlbumData): void => {
          setSelectedAlbum(album);
        }}
      />
    );
  };

  return <Panel className="full-screen" panelHeader={getHeader()} panelContent={getContent()} panelFooter={getFooter()} />;
}
